import type { LucideIcon } from "lucide-react";
import { Link } from "react-router";
import { Card, CardContent } from "@/components/ui/Card";
import {
  TruncatedText,
  iconTileToneClass,
  type IconTileTone,
} from "@/components/ui";
import { cn } from "@/lib/utils";

interface StatCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  trend?: {
    value: number;
    isPositive: boolean;
    label?: string;
  };
  subtitle?: string;
  iconColor?: IconTileTone;
  selected?: boolean;
  /** Data still loading: shows a placeholder instead of `value` and `subtitle`. */
  loading?: boolean;
  /** When set, the whole card navigates to this path. */
  to?: string;
  /** Accessible name for the link (defaults to `title`). */
  linkAriaLabel?: string;
  className?: string;
}

/** Wrapper for clickable StatCard filters — focus ring only on keyboard focus, not selection. */
export const selectableStatCardButtonClass =
  "w-full rounded-xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2";

/** Soft corner glow that ties the card to its icon color. */
const glowColorClasses: Record<IconTileTone, string> = {
  primary: "bg-primary-400/20",
  accent: "bg-accent-400/20",
  success: "bg-success-400/20",
  warning: "bg-warning-400/20",
  error: "bg-error-400/20",
};

export function StatCard({
  title,
  value,
  icon: Icon,
  trend,
  subtitle,
  iconColor = "primary",
  selected = false,
  loading = false,
  to,
  linkAriaLabel,
  className,
}: StatCardProps) {
  const card = (
    <Card
      aria-busy={loading || undefined}
      className={cn(
        "group relative h-full overflow-hidden transition-[border-color,box-shadow] duration-200 hover:border-surface-300 hover:shadow-elevated",
        selected && "border-primary-500 ring-1 ring-primary-500 hover:border-primary-500",
        to && "cursor-pointer",
        className,
      )}
    >
      <div
        className={cn(
          "pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full opacity-0 blur-2xl transition-opacity duration-300 group-hover:opacity-100",
          glowColorClasses[iconColor],
          selected && "opacity-100",
        )}
        aria-hidden
      />
      <CardContent className="relative p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3">
          <TruncatedText as="p" className="text-[13px] font-medium text-surface-500">
            {title}
          </TruncatedText>
          <div
            className={cn(
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset",
              iconTileToneClass(iconColor),
            )}
          >
            <Icon className="h-4 w-4" aria-hidden />
          </div>
        </div>
        {loading ? (
          <div aria-hidden>
            <div className="mt-2 h-8 w-28 animate-pulse rounded-md bg-surface-100 sm:h-9" />
            <div className="mt-2 h-3 w-16 animate-pulse rounded bg-surface-100" />
          </div>
        ) : (
          <>
            <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <p className="text-2xl font-semibold tabular-nums tracking-tight text-surface-900 sm:text-[28px] sm:leading-9">
                {value}
              </p>
              {trend ? (
                <span
                  className={cn(
                    "inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-medium tabular-nums",
                    trend.isPositive
                      ? "bg-success-50 text-success-700"
                      : "bg-error-50 text-error-700",
                  )}
                >
                  {trend.isPositive ? "+" : "-"}
                  {Math.abs(trend.value)}%
                  {trend.label ? (
                    <span className="ml-1 hidden font-normal text-surface-500 sm:inline">
                      {trend.label}
                    </span>
                  ) : null}
                </span>
              ) : null}
            </div>
            {subtitle ? (
              <p className="mt-1 text-xs text-surface-500">{subtitle}</p>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );

  if (!to) {
    return card;
  }

  return (
    <Link
      to={to}
      aria-label={linkAriaLabel ?? title}
      className={cn(selectableStatCardButtonClass, "block h-full")}
    >
      {card}
    </Link>
  );
}
